import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { healthtrackApi } from '../../services/healthtrackApi';
import { Shield, RefreshCw } from 'lucide-react';
import logger from '../../utils/logger';
import {
  OverviewTab, AbhaTab, EligibilityTab,
  RecordsTab, ClaimsTab, ConsentsTab
} from './abdm/ABDMTabPanels';
import { RegistrationModal, LinkingModal, ClaimFormModal } from './abdm/ABDMModals';

const TABS = [
  { id: 'overview', label: 'Overview', icon: Shield },
  { id: 'abha', label: 'ABHA' },
  { id: 'eligibility', label: 'Eligibility' },
  { id: 'records', label: 'Health Records' },
  { id: 'claims', label: 'Claims' },
  { id: 'consents', label: 'Consents' },
];

const ABDMIntegration = ({ patientId, isDoctor = false }) => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [abdmStatus, setAbdmStatus] = useState(null);
  const [linkedAbha, setLinkedAbha] = useState(null);
  const [consents, setConsents] = useState([]);
  const [eligibility, setEligibility] = useState(null);
  const [claims, setClaims] = useState([]);
  const [nationalRecords, setNationalRecords] = useState([]);

  const [showRegistration, setShowRegistration] = useState(false);
  const [showLinking, setShowLinking] = useState(false);
  const [showClaimForm, setShowClaimForm] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [txnId, setTxnId] = useState('');

  const [registrationForm, setRegistrationForm] = useState({
    first_name: '', middle_name: '', last_name: '',
    date_of_birth: '', gender: 'M', mobile: '', email: ''
  });
  const [linkForm, setLinkForm] = useState({ abha_number: '', abha_address: '' });
  const [claimForm, setClaimForm] = useState({
    claim_type: 'preauth', diagnosis_codes: ['I10'], procedure_codes: ['99213'],
    package_code: 'HBP-01', package_name: 'General Medicine', estimated_amount: 25000,
    treatment_details: { admission_date: new Date().toISOString().split('T')[0], treatment_type: 'OPD' }
  });

  useEffect(() => { loadABDMData(); }, [patientId]); // eslint-disable-line

  const loadABDMData = async () => {
    setLoading(true);
    try {
      const status = await healthtrackApi.abdm.getStatus();
      setAbdmStatus(status);
      if (patientId) {
        try {
          const abhaData = await healthtrackApi.abdm.getPatientAbha(patientId);
          if (abhaData.linked) {
            setLinkedAbha(abhaData);
            try { const e = await healthtrackApi.abdm.getEligibilityStatus(patientId); if (e.is_eligible !== undefined) setEligibility(e); } catch {}
            try { const c = await healthtrackApi.abdm.getPatientClaims(patientId); setClaims(c.claims || []); } catch {}
            try { const r = await healthtrackApi.abdm.getNationalRecords(patientId); setNationalRecords(r.records || []); } catch {}
          }
          const consentData = await healthtrackApi.abdm.getPatientConsents(patientId);
          setConsents(consentData.consents || []);
        } catch { logger.log('No ABHA linked yet'); }
      }
      if (isDoctor) {
        try { await healthtrackApi.abdm.getClaimsDashboard(); } catch {}
      }
    } catch (error) { logger.error('Failed to load ABDM data:', error); }
    finally { setLoading(false); }
  };

  const handleGenerateOTP = async () => {
    try { const r = await healthtrackApi.abdm.generateOtp(registrationForm.mobile); setTxnId(r.txn_id); setOtpSent(true); alert(`OTP sent! Demo OTP: ${r.demo_otp}`); }
    catch { alert('Failed to send OTP'); }
  };

  const handleRegisterABHA = async () => {
    try {
      const r = await healthtrackApi.abdm.registerAbha(registrationForm);
      alert(`ABHA Created!\nNumber: ${r.abha_number}\nAddress: ${r.abha_address}`);
      setShowRegistration(false);
      if (patientId) { await healthtrackApi.abdm.linkPatient({ patient_id: patientId, abha_number: r.abha_number, abha_address: r.abha_address }); loadABDMData(); }
    } catch { alert('Failed to register ABHA'); }
  };

  const handleLinkABHA = async () => {
    try { await healthtrackApi.abdm.linkPatient({ patient_id: patientId, ...linkForm }); alert('ABHA linked successfully!'); setShowLinking(false); loadABDMData(); }
    catch { alert('Failed to link ABHA'); }
  };

  const handleConsentAction = async (consentId, action) => {
    try {
      if (action === 'approve') await healthtrackApi.abdm.approveConsent(consentId);
      else if (action === 'deny') await healthtrackApi.abdm.denyConsent(consentId);
      else if (action === 'revoke') await healthtrackApi.abdm.revokeConsent(consentId);
      loadABDMData();
    } catch { alert(`Failed to ${action} consent`); }
  };

  const handleCheckEligibility = async () => {
    try { const r = await healthtrackApi.abdm.checkEligibility({ patient_id: patientId, abha_number: linkedAbha?.abha_number, scheme: 'PMJAY' }); setEligibility(r); alert(r.is_eligible ? 'Patient is eligible for Ayushman Bharat!' : 'Patient is not eligible'); }
    catch { alert('Failed to check eligibility'); }
  };

  const handleFetchNationalRecords = async () => {
    if (!linkedAbha || consents.filter(c => c.status === 'GRANTED').length === 0) { alert('Need linked ABHA and active consent to fetch records'); return; }
    try {
      const gc = consents.find(c => c.status === 'GRANTED');
      const r = await healthtrackApi.abdm.fetchNationalRecords({ patient_id: patientId, abha_number: linkedAbha.abha_number, consent_id: gc.id, hi_types: ['OPConsultation', 'Prescription', 'DiagnosticReport'] });
      setNationalRecords(r.records || []);
    } catch { alert('Failed to fetch national records'); }
  };

  const handleSubmitClaim = async () => {
    try {
      const r = await healthtrackApi.abdm.submitClaim({ patient_id: patientId, abha_number: linkedAbha.abha_number, ...claimForm });
      alert(`Claim submitted!\nClaim Number: ${r.claim_number}\nStatus: ${r.status}`);
      setShowClaimForm(false); loadABDMData();
    } catch (e) { alert('Failed to submit claim: ' + e.message); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-48"><div className="animate-spin rounded-full h-10 w-10 border-4 border-green-500 border-t-transparent" /></div>;
  }

  const tabProps = { linkedAbha, eligibility, consents, nationalRecords, claims, handleCheckEligibility, handleFetchNationalRecords, handleConsentAction, setActiveTab, setShowClaimForm, setShowRegistration, setShowLinking };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-teal-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-lg"><Shield className="w-8 h-8" /></div>
            <div><h2 className="text-2xl font-bold">ABDM Integration</h2><p className="text-green-100">Ayushman Bharat Digital Mission</p></div>
          </div>
          <Button variant="outline" className="text-white border-white hover:bg-white/20" onClick={loadABDMData}>
            <RefreshCw className="w-4 h-4 mr-2" />Refresh
          </Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
          {[
            { label: 'ABHA Linked', val: linkedAbha ? '\u2713' : '\u2717' },
            { label: 'PMJAY Eligible', val: eligibility?.is_eligible ? '\u2713' : '?' },
            { label: 'Active Consents', val: consents.filter(c => c.status === 'GRANTED').length },
            { label: 'Health Records', val: nationalRecords.length },
            { label: 'Claims', val: claims.length },
          ].map(s => (
            <div key={s.label} className="bg-white/10 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold">{s.val}</div>
              <div className="text-sm text-green-100">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border">
        <div className="flex overflow-x-auto border-b">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === tab.id ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {tab.label}
            </button>
          ))}
        </div>
        <div className="p-6">
          {activeTab === 'overview' && <OverviewTab {...tabProps} />}
          {activeTab === 'abha' && <AbhaTab {...tabProps} />}
          {activeTab === 'eligibility' && <EligibilityTab {...tabProps} />}
          {activeTab === 'records' && <RecordsTab {...tabProps} />}
          {activeTab === 'claims' && <ClaimsTab {...tabProps} />}
          {activeTab === 'consents' && <ConsentsTab {...tabProps} />}
        </div>
      </div>

      {/* Modals */}
      {showRegistration && <RegistrationModal registrationForm={registrationForm} setRegistrationForm={setRegistrationForm} otpSent={otpSent} handleGenerateOTP={handleGenerateOTP} handleRegisterABHA={handleRegisterABHA} onClose={() => setShowRegistration(false)} />}
      {showLinking && <LinkingModal linkForm={linkForm} setLinkForm={setLinkForm} handleLinkABHA={handleLinkABHA} onClose={() => setShowLinking(false)} />}
      {showClaimForm && <ClaimFormModal claimForm={claimForm} setClaimForm={setClaimForm} eligibility={eligibility} handleSubmitClaim={handleSubmitClaim} onClose={() => setShowClaimForm(false)} />}
    </div>
  );
};

export default ABDMIntegration;
